import {useQuery} from '@github-ui/react-query'
import {FetchCAPIError} from '../utils/fetch-capi'
import {useConsecutiveCAPI401sContext} from '../contexts/ConsecutiveCAPI401sContext'
import {AgentSessionsTokenProvider} from '@github-ui/copilot-auth-token/agent-sessions-token'
import {useEffect} from 'react'

const MAX_401_RETRIES = 5

type UseQueryArgs<T> = Parameters<typeof useQuery<T>>

export function useCAPIQuery<T>(...args: UseQueryArgs<T>) {
  const result = useQuery(...args)
  const {numberOf401s, increment401s, reset401s} = useConsecutiveCAPI401sContext()
  const {isError, isLoading, error} = result

  useEffect(() => {
    // Why are we doing this? It's possible for the tokens we use to contact CAPI
    // to become unusable before their actual expiration time. This happens when a user
    // has too many concurrent tokens. This logic clears the cached token if it starts
    // returning 401s, demonstrating it is no longer usable.
    if (isError && error instanceof FetchCAPIError && error.status === 401) {
      // If we've already reached the max number of consecutive 401s, clear the token cache to fetch a new one
      if (numberOf401s >= MAX_401_RETRIES) {
        const tokenProvider = new AgentSessionsTokenProvider()
        tokenProvider.removeLocalStorageAuthToken()
        reset401s()
      } else {
        // Otherwise increment the count
        increment401s()
      }
    } else if (!isError && !isLoading && numberOf401s > 0) {
      // If the query was successful, reset the count if there were any previous failures
      reset401s()
    }
  }, [isError, isLoading, error, increment401s, reset401s, numberOf401s])

  return result
}
